/**
 * Reverse projection: Coverage Matrix authored schema values → SED field paths.
 *
 * Single source of truth paired with `REQUIREMENT_FIELD_ALIGNMENT` (SED → schema).
 * Used by overlay, variable browser display, and bootstrap section projection.
 */
import type { ExtractedClaim } from '../../types/siteContext';
import type { FieldPath } from '../evidenceLineage/requirementToFields';
import { isAuthoredValuePresent } from '../../types/authoredEvidence';
import {
  getFieldAlignment,
  type RequirementFieldAlignment,
} from './requirementFieldAlignment';

export interface SedFieldPatch {
  fieldPath: FieldPath;
  value: unknown;
}

function stringOrUndef(v: unknown): string | undefined {
  if (typeof v !== 'string') return undefined;
  const trimmed = v.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function numberOrUndef(v: unknown): number | undefined {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

function booleanOrUndef(v: unknown): boolean | undefined {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'string') {
    const s = v.toLowerCase().trim();
    if (s === 'true' || s === 'yes') return true;
    if (s === 'false' || s === 'no') return false;
  }
  return undefined;
}

function listToStrings(v: unknown): string[] | undefined {
  if (Array.isArray(v)) {
    const out = v.map(String).map(s => s.trim()).filter(Boolean);
    return out.length > 0 ? out : undefined;
  }
  if (typeof v === 'string') {
    const parts = v.split(',').map(s => s.trim()).filter(Boolean);
    return parts.length > 0 ? parts : undefined;
  }
  return undefined;
}

/** Coerce an authored schema value onto the SED payload key at `fieldPath`. */
export function normalizeAuthoredValueForSedField(
  fieldPath: FieldPath,
  rawValue: unknown,
): unknown {
  const sedKey = fieldPath.split('.').slice(1).join('.') || fieldPath;
  switch (sedKey) {
    case 'landUse': {
      const list = listToStrings(rawValue);
      return list ? list.join(', ') : stringOrUndef(rawValue);
    }
    case 'species':
      return listToStrings(rawValue) ?? (Array.isArray(rawValue) ? rawValue : undefined);
    case 'hasWetlands':
    case 'hasCriticalHabitat':
    case 'sfha':
      return booleanOrUndef(rawValue);
    case 'fiberBuildDistanceFt': {
      if (typeof rawValue === 'string' && rawValue.trim() === '') return undefined;
      const value = numberOrUndef(rawValue);
      return value !== undefined && value >= 0 ? value : undefined;
    }
    case 'wetlandAcres':
    case 'acres':
    case 'retailRate':
    case 'nearestFiberKm':
      return numberOrUndef(rawValue);
    default:
      if (typeof rawValue === 'number' || typeof rawValue === 'boolean') return rawValue;
      if (typeof rawValue === 'string') return stringOrUndef(rawValue) ?? rawValue;
      return rawValue;
  }
}

function floodZoneSideEffects(zone: string): SedFieldPatch[] {
  const sfha = ['A', 'AE', 'AH', 'AO', 'V', 'VE'].includes(zone);
  const floodRisk = sfha ? (zone.startsWith('V') ? 'High' : 'Medium') : 'Low';
  return [
    { fieldPath: 'flood.floodZone', value: zone },
    { fieldPath: 'flood.sfha', value: sfha },
    { fieldPath: 'flood.floodRisk', value: floodRisk },
  ];
}

/* ── P6 — municipal utility letter → waterResource sub-objects ──────── */

type MunicipalSubObject = Record<string, unknown>;

const MUNICIPAL_SUPPLY_KEYS: Record<string, string> = {
  supplyProvider: 'provider',
  supplySourceDescription: 'sourceDescription',
  supplyAvgMgd: 'avgMgd',
  supplyPeakMgd: 'peakMgd',
  supplyPermittedMgd: 'permittedMgd',
  supplyExcessMgd: 'excessMgd',
  supplyStorageGallons: 'storageGallons',
  supplyNearestMainMiles: 'nearestMainMiles',
  supplyLineToSite: 'lineToSite',
};
const WASTEWATER_KEYS: Record<string, string> = {
  wastewaterProvider: 'provider',
  wastewaterPlantDescription: 'sourceDescription',
  wastewaterPermittedMgd: 'permittedMgd',
  wastewaterAvgMgd: 'avgMgd',
  wastewaterPeakMgd: 'peakMgd',
  wastewaterExcessMgd: 'excessMgd',
  wastewaterNearestConnectionMiles: 'nearestMainMiles',
  wastewaterLineToSite: 'lineToSite',
  wastewaterConnectionType: 'connectionType',
};
const MUNICIPAL_NUMBER_FIELDS = new Set(['avgMgd', 'peakMgd', 'permittedMgd', 'excessMgd', 'storageGallons', 'nearestMainMiles']);
const MUNICIPAL_APPLIES = new Set(['this-site', 'other-project', 'unknown']);
const MUNICIPAL_CONNECTION = new Set(['gravity', 'force-main']);

function authoredBoolean(v: unknown): boolean | undefined {
  if (typeof v === 'boolean') return v;
  const s = stringOrUndef(v)?.toLowerCase();
  if (s === 'true' || s === 'yes') return true;
  if (s === 'false' || s === 'no') return false;
  return undefined;
}

function municipalSubObject(
  fields: Record<string, unknown>,
  keyMap: Record<string, string>,
  provenance: Record<string, unknown> | null,
): MunicipalSubObject | null {
  const out: MunicipalSubObject = {
    provider: null, avgMgd: null, peakMgd: null, permittedMgd: null, excessMgd: null,
    nearestMainMiles: null, lineToSite: null, connectionType: null,
  };
  let substantive = false;
  for (const [schemaKey, target] of Object.entries(keyMap)) {
    const raw = fields[schemaKey];
    if (!isAuthoredValuePresent(raw)) continue;
    let value: unknown;
    if (MUNICIPAL_NUMBER_FIELDS.has(target)) value = numberOrUndef(raw);
    else if (target === 'lineToSite') value = authoredBoolean(raw);
    else if (target === 'connectionType') {
      const s = stringOrUndef(raw)?.toLowerCase().replace(/\s+/g, '-');
      value = s && MUNICIPAL_CONNECTION.has(s) ? s : undefined;
    } else value = stringOrUndef(raw);
    if (value === undefined) continue;
    out[target] = value;
    substantive = true;
  }
  if (!substantive) return null;
  if (provenance) out.provenance = provenance;
  return out;
}

function municipalProvenance(fields: Record<string, unknown>): Record<string, unknown> | null {
  const statedBy = stringOrUndef(fields.statedBy);
  const statedOn = stringOrUndef(fields.statedOn);
  const appliesRaw = stringOrUndef(fields.appliesToProject)?.toLowerCase().replace(/\s+/g, '-');
  const appliesToProject = appliesRaw && MUNICIPAL_APPLIES.has(appliesRaw) ? appliesRaw : 'unknown';
  if (!statedBy && !statedOn && appliesToProject === 'unknown') return null;
  return { statedBy: statedBy ?? null, statedOn: statedOn ?? null, appliesToProject };
}

/**
 * The whole claim is assembled from `allFields` (every structured field the
 * user typed); a legacy single-key call still yields the object for that
 * key. `collectSedPatchesForClaim` dedupes by fieldPath, so every key
 * producing the same complete object collapses to one patch per sub-object.
 */
function municipalUtilityPatches(
  schemaKey: string,
  rawValue: unknown,
  allFields: Record<string, unknown> | undefined,
): SedFieldPatch[] {
  const fields = allFields ?? { [schemaKey]: rawValue };
  const provenance = municipalProvenance(fields);
  const supply = municipalSubObject(fields, MUNICIPAL_SUPPLY_KEYS, provenance);
  const wastewater = municipalSubObject(fields, WASTEWATER_KEYS, provenance);
  const out: SedFieldPatch[] = [];
  if (supply) out.push({ fieldPath: 'waterResource.municipalSupply', value: supply });
  if (wastewater) out.push({ fieldPath: 'waterResource.wastewaterService', value: wastewater });
  return out;
}

function transformAuthoredToSedPatches(
  schemaKey: string,
  rawValue: unknown,
  transformId: string | undefined,
  allFields?: Record<string, unknown>,
): SedFieldPatch[] {
  if (!isAuthoredValuePresent(rawValue)) return [];
  switch (transformId) {
    case 'mapMunicipalUtilityToWaterResource':
      return municipalUtilityPatches(schemaKey, rawValue, allFields);
    case 'mapGridDataToCoverage':
      if (schemaKey === 'capacityOffered_MW') {
        const mw = numberOrUndef(rawValue);
        if (mw === undefined) return [];
        const bucket = mw >= 50 ? 'High' : mw >= 20 ? 'Limited' : 'Constrained';
        return [
          // Legacy ordinal band — unchanged thresholds; existing readers keep
          // working.
          { fieldPath: 'grid.capacity', value: bucket },
          // The stated MW itself — previously DESTROYED by the ordinal
          // collapse. ONE single-segment patch carrying the whole object
          // (the section patch merger drops nested paths, so this must be
          // atomic, never `grid.capacityDetail.mw`). A letter figure is an
          // INDICATION, not a commitment — the basis says so.
          {
            fieldPath: 'grid.capacityDetail',
            value: { mw, basis: 'utility-indication', enteredVia: 'utility-letter' },
          },
        ];
      }
      if (schemaKey === 'interconnectionClass') {
        const cls = stringOrUndef(rawValue);
        return cls ? [{ fieldPath: 'grid.interconnectionClass', value: cls }] : [];
      }
      return [];
    default:
      return [];
  }
}

function patchesForAlignmentRow(
  row: RequirementFieldAlignment,
  rawValue: unknown,
  allFields?: Record<string, unknown>,
): SedFieldPatch[] {
  if (row.kind === 'doc-only' || !isAuthoredValuePresent(rawValue)) return [];

  if (row.kind === 'transform') {
    return transformAuthoredToSedPatches(row.schemaKey, rawValue, row.transformId, allFields);
  }

  if (!row.fieldPath) return [];

  if (row.schemaKey === 'floodZone' && row.fieldPath === 'flood.floodZone') {
    const zone = stringOrUndef(rawValue);
    return zone ? floodZoneSideEffects(zone) : [];
  }

  const value = normalizeAuthoredValueForSedField(row.fieldPath, rawValue);
  if (row.fieldPath === 'connectivity.fiberBuildDistanceFt' && value === undefined) return [];
  const patches: SedFieldPatch[] = [{ fieldPath: row.fieldPath, value }];
  for (const fb of row.fallbackFieldPaths ?? []) {
    patches.push({ fieldPath: fb, value });
  }
  return patches;
}

/** Map one authored schema field to zero or more SED patches. */
export function authoredSchemaValueToSedPatches(
  requirementId: string,
  schemaKey: string,
  rawValue: unknown,
  allFields?: Record<string, unknown>,
): SedFieldPatch[] {
  const row = getFieldAlignment(requirementId, schemaKey);
  if (!row) return [];
  return patchesForAlignmentRow(row, rawValue, allFields);
}

/** All SED patches for a user-typed structured claim. */
export function collectSedPatchesForClaim(claim: ExtractedClaim): SedFieldPatch[] {
  if (claim.source !== 'user-typed' || !claim.requirementId || !claim.structuredFields) {
    return [];
  }
  const seen = new Set<string>();
  const out: SedFieldPatch[] = [];
  for (const [schemaKey, rawValue] of Object.entries(claim.structuredFields)) {
    for (const patch of authoredSchemaValueToSedPatches(claim.requirementId, schemaKey, rawValue, claim.structuredFields)) {
      if (seen.has(patch.fieldPath)) continue;
      seen.add(patch.fieldPath);
      out.push(patch);
    }
  }
  return out;
}

/** Merge patches targeting one SED section into a partial section payload. */
export function mergeSedPatchesForSection(
  sectionId: string,
  patches: readonly SedFieldPatch[],
): Record<string, unknown> | undefined {
  const data: Record<string, unknown> = {};
  for (const patch of patches) {
    const head = patch.fieldPath.split('.')[0];
    if (head !== sectionId) continue;
    const key = patch.fieldPath.split('.').slice(1).join('.');
    if (!key || key.includes('.')) continue;
    data[key] = patch.value;
  }
  return Object.keys(data).length > 0 ? data : undefined;
}
